import { computed, onMounted, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import Swal from "sweetalert2";

import reportService, { type ProductQuantityAlertReportItem } from "@/services/report";
import warehouseService, { type Warehouse } from "@/services/warehouse";

type ProductQuantityAlertFilters = {
  warehouse_id: number | null;
  per_page: number;
};

const createInitialFilters = (): ProductQuantityAlertFilters => ({
  warehouse_id: null,
  per_page: 10,
});

export function useProductQuantityAlertReport() {
  const { t } = useI18n();
  const alerts = ref<ProductQuantityAlertReportItem[]>([]);
  const warehouses = ref<Warehouse[]>([]);
  const loading = ref(false);
  const searchQuery = ref("");
  const currentPage = ref(1);
  const total = ref(0);
  const filters = reactive<ProductQuantityAlertFilters>(createInitialFilters());
  const i18nScope = "product_quantity_alert_report_page";
  const pageTitle = `${i18nScope}.title`;

  const pagination = computed(() => ({
    current: currentPage.value,
    pageSize: filters.per_page,
    total: total.value,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "30", "50", "100"],
    showTotal: (total: number, range: [number, number]) =>
      t(`${i18nScope}.pagination_total`, { start: range[0], end: range[1], total }),
    locale: { items_per_page: t(`${i18nScope}.items_per_page`) },
  }));

  const queryParams = computed(() => ({
    page: currentPage.value,
    per_page: filters.per_page,
    search_query: searchQuery.value,
    warehouse_id: filters.warehouse_id || undefined,
  }));

  const fetchWarehouses = async () => {
    try {
      warehouses.value = await warehouseService.getWarehouses();
    } catch (error) {
      console.error("Error loading warehouses for quantity alert report:", error);
    }
  };

  const fetchProductQuantityAlerts = async () => {
    loading.value = true;
    try {
      const response = await reportService.getProductQuantityAlertReports(queryParams.value);
      alerts.value = response.data ?? [];
      currentPage.value = response.current_page;
      total.value = response.total;
    } catch (error) {
      console.error("Error fetching product quantity alert reports:", error);
      Swal.fire({
        icon: "error",
        title: t(`${i18nScope}.load_failed`),
        text: t(`${i18nScope}.unable_to_load_report`),
      });
    } finally {
      loading.value = false;
    }
  };

  const handleTableChange = (page: number, pageSize: number) => {
    currentPage.value = page;

    if (pageSize !== filters.per_page) {
      filters.per_page = pageSize;
      currentPage.value = 1;
    }

    fetchProductQuantityAlerts();
  };

  const applyFilters = () => {
    currentPage.value = 1;
    fetchProductQuantityAlerts();
  };

  const resetFilters = () => {
    Object.assign(filters, createInitialFilters());
    currentPage.value = 1;
    fetchProductQuantityAlerts();
  };

  watch(searchQuery, () => {
    currentPage.value = 1;
    fetchProductQuantityAlerts();
  });

  onMounted(async () => {
    await fetchWarehouses();
    await fetchProductQuantityAlerts();
  });

  return {
    pageTitle,
    i18nScope,
    alerts,
    warehouses,
    loading,
    searchQuery,
    filters,
    pagination,
    total,
    applyFilters,
    resetFilters,
    handleTableChange,
  };
}
